/// <amd-module name="Repos/Tfvc/ChangesetsList/ChangesetsListDataProviderService" />
import { ChangeList, ChangeListSearchCriteria, HistoryEntry, TfvcItem } from "Repos/Common/Contracts/Git";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { IVssContributionService } from "VSS/Platform/Contribution";

export interface IChangesetsListDataProviderService extends IVssService {
    fetchChangesets(searchCriteria: ChangeListSearchCriteria): Promise<ChangesetsListResult | undefined>;
    getInitialChangesets(): ChangesetsListResult | undefined;
}

export interface TfvcChangeList extends ChangeList<TfvcItem> {
    changesetId: number;
    isShelveset: boolean;
    ownerRef: IdentityRef;
    shelvesetName: string;
}

export interface TfvcHistoryEntry extends HistoryEntry<TfvcItem> {
    changeList: TfvcChangeList;
}

export interface ChangesetsListResult {
    hasMore: boolean;
    searchCancelled: boolean;
    changesets: TfvcHistoryEntry[];
}

interface HistoryQueryResults {
    changesets: {
        moreResultsAvailable: boolean;
        results: TfvcHistoryEntry[];
    };
    searchCriteria: ChangeListSearchCriteria;
}

const changesetsListDataProviderKey = "ms.vss-code-web.changesets-list-data-provider";

class ChangesetsListDataProviderService extends VssService implements IChangesetsListDataProviderService {
    private latestRequestKey: number = 0;

    public async fetchChangesets(searchCriteria: ChangeListSearchCriteria): Promise<ChangesetsListResult | undefined> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");

        this.latestRequestKey++;
        const requestKey = this.latestRequestKey;

        const data = await contributionService.getDataAsync<HistoryQueryResults>(
            changesetsListDataProviderKey,
            { ...searchCriteria, changeListType: "changesets" },
            true
        );

        return data && this.processData(data, requestKey);
    }

    public getInitialChangesets(): ChangesetsListResult | undefined {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const data = contributionService.getData<HistoryQueryResults>(changesetsListDataProviderKey);
        return data && data.changesets && this.processData(data, this.latestRequestKey);
    }

    private processData(data: HistoryQueryResults, requestKey: number): ChangesetsListResult {
        return {
            changesets: data.changesets.results || [],
            hasMore: Boolean(data.changesets.moreResultsAvailable),
            searchCancelled: !(this.latestRequestKey === requestKey)
        };
    }
}

export function getChangesetsListDataProviderService(context: IVssPageContext) {
    return context.getService<IChangesetsListDataProviderService>("IChangesetsListDataProviderService");
}

Services.add("IChangesetsListDataProviderService", { serviceFactory: ChangesetsListDataProviderService });
